import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement } from 'react';
import styled from 'styled-components';
import { BATTLESHIP, GYPSUM } from 'HubStyleTokens/colors';
import FormattedCurrency from 'I18n/components/FormattedCurrency';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Small from 'UIComponents/elements/Small';
import UIBadge from 'UIComponents/badge/UIBadge';
import UICardHero from 'UIComponents/card/UICardHero';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import { formatCentsToDollars } from 'marketplace-ui-common/utils/currency';
import { STAR_RATING_FILLED } from 'marketplace-ui-reviews/constants/RatingTypes';
import { MINIMUM_REVIEWS_FOR_DISPLAY } from 'marketplace-ui-reviews/constants/ReviewsListQueries';
import Rating from 'marketplace-ui-reviews/components/Rating';
import * as ScreenSizes from '../../constants/layout/ScreenSizes';
import { useResponsiveProps } from '../../hooks/useResponsiveProps';
const THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS = 'theme-card__thumbnail-placeholder__flex';
const ThumbnailPlaceholder = styled(props => /*#__PURE__*/_jsx("div", Object.assign({}, props, {
  children: /*#__PURE__*/_jsx(UIFlex, {
    className: THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS,
    align: "center",
    justify: "center",
    children: /*#__PURE__*/_jsx(UIIcon, {
      color: BATTLESHIP,
      name: "insertImage",
      size: 120
    })
  })
}))).withConfig({
  displayName: "AssetCard__ThumbnailPlaceholder",
  componentId: "sc-1dvhds2-0"
})(["position:relative;width:100%;padding-top:75%;background-color:", ";border-top-left-radius:inherit;border-top-right-radius:inherit;overflow:hidden;.", "{position:absolute;top:0;left:0;bottom:0;right:0;}"], GYPSUM, THUMBNAIL_PLACEHOLDER_FLEX_CONTAINER_CLASS);
ThumbnailPlaceholder.displayName = 'ThumbnailPlaceholder';
const StyledPrice = styled(AbstractTextElement).withConfig({
  displayName: "AssetCard__StyledPrice",
  componentId: "sc-1dvhds2-1"
})(["font-size:20px;"]);
StyledPrice.displayName = 'StyledPrice';
export default function AssetCard({
  className,
  companyName,
  detailsButton,
  demoButton,
  name,
  priceCents,
  providerName,
  reviewSummary,
  thumbnailUrl,
  offeringType
}) {
  const {
    isMobileView
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    }
  });
  const renderedPrice = priceCents != null ? /*#__PURE__*/_jsx(StyledPrice, {
    weight: "medium",
    className: "p-x-2",
    children: priceCents === 0 ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystemMarketplaceCore.cards.assetCard.planType.free"
    }) : /*#__PURE__*/_jsx(FormattedCurrency, {
      currencyCode: "USD",
      simplifyCurrency: true,
      value: formatCentsToDollars(priceCents)
    })
  }) : null;

  const renderedOfferingMetaInfo = /*#__PURE__*/_jsxs(UIFlex, {
    direction: "column",
    gap: 4,
    className: "p-top-2 p-bottom-2",
    style: {
      width: '100%'
    },
    children: [/*#__PURE__*/_jsx("div", {
      className: "is--heading-4 m-bottom-0",
      children: name
    }), /*#__PURE__*/_jsx("div", {
      children: /*#__PURE__*/_jsx(Small, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ecosystemMarketplaceCore.cards.assetCard.companyName",
          options: {
            companyName: providerName || companyName || ''
          }
        })
      })
    }), /*#__PURE__*/_jsxs(UIFlex, {
      justify: "between",
      style: {
        width: '100%'
      },
      className: "p-top-1",
      children: [/*#__PURE__*/_jsx(UIBadge, {
        use: offeringType === 'THEME' ? 'thunderdome' : 'norman',
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `ecosystemMarketplaceCore.cards.assetCard.offeringType.${offeringType}`
        })
      }), renderedPrice]
    })]
  });

  const renderedReviewSummary = reviewSummary && reviewSummary.reviewCount >= MINIMUM_REVIEWS_FOR_DISPLAY ? /*#__PURE__*/_jsx(Rating, {
    rating: reviewSummary.overallRating,
    label: reviewSummary.reviewCount,
    use: "card",
    type: STAR_RATING_FILLED
  }) : /*#__PURE__*/_jsx("span", {});
  return /*#__PURE__*/_jsxs(UICardWrapper, {
    className: className,
    compact: true,
    "data-test-id": "assetcard-wrapper",
    children: [thumbnailUrl ? /*#__PURE__*/_jsx(UICardHero, {
      children: /*#__PURE__*/_jsx(UIImage, {
        src: thumbnailUrl,
        alt: "thumbnail_image",
        style: {
          maxHeight: 360
        }
      })
    }) : /*#__PURE__*/_jsx(ThumbnailPlaceholder, {}), /*#__PURE__*/_jsxs(UICardSection, {
      scrollable: true,
      className: "p-all-5",
      style: {
        flexGrow: 0,
        marginTop: 'auto'
      },
      children: [/*#__PURE__*/_jsx(UIFlex, {
        direction: "column",
        align: "start",
        justify: "between",
        children: renderedOfferingMetaInfo
      }), /*#__PURE__*/_jsxs(UIFlex, {
        justify: "between",
        align: "end",
        children: [renderedReviewSummary, (detailsButton || demoButton) && /*#__PURE__*/_jsxs(UIFlex, {
          direction: "row",
          align: "center",
          className: "p-top-1 p-left-2",
          wrap: isMobileView ? 'wrap' : 'nowrap',
          justify: "end",
          children: [detailsButton && /*#__PURE__*/cloneElement(detailsButton, {
            size: 'small'
          }), demoButton && /*#__PURE__*/cloneElement(demoButton, {
            size: 'small'
          })]
        })]
      })]
    })]
  });
}